<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <router-link to="/admissions/sources" class="btn btn-sm btn-outline-secondary mr-3">
                    <icon name="chevron-left"></icon> Source files
                </router-link>
                <span class="text-muted mr-3">
                    <code>{{ file }}</code>
                    <span v-if="day">— play date <strong>{{ day }}</strong>, {{ revisions.length }} revisions</span>
                </span>
                <template v-if="revisions.length">
                    <label class="mb-0 mr-1 text-muted">From</label>
                    <select v-model="from" class="form-control form-control-sm w-auto mr-3" @change="reload">
                        <option :value="null">(nothing)</option>
                        <option v-for="rev in revisions" :key="'from-' + rev.hash" :value="rev.hash">
                            {{ revisionLabel(rev) }}
                        </option>
                    </select>
                    <label class="mb-0 mr-1 text-muted">To</label>
                    <select v-model="to" class="form-control form-control-sm w-auto" @change="reload">
                        <option v-for="rev in revisions" :key="'to-' + rev.hash" :value="rev.hash">
                            {{ revisionLabel(rev) }}
                        </option>
                    </select>
                </template>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!revisions.length" class="box">
            <div class="box-content text-center text-muted py-4">No git history found for this file.</div>
        </div>

        <div v-else class="box">
            <div class="box-content">
                <p class="mb-2">
                    <span class="badge badge-success mr-2">{{ summary.added }} added</span>
                    <span class="badge badge-danger mr-2">{{ summary.removed }} removed</span>
                    <span class="badge badge-warning mr-2">{{ summary.changed }} changed</span>
                    <span class="text-muted">
                        Admissions in the affected rows:
                        {{ formatInt(summary.admissions_before) }} → {{ formatInt(summary.admissions_after) }}
                        (<strong :class="deltaClass(summary.admissions_after - summary.admissions_before)">{{ formatDelta(summary.admissions_after - summary.admissions_before) }}</strong>)
                    </span>
                </p>

                <div v-if="!rows.length" class="text-center text-muted py-4">
                    No row-level differences between these two revisions.
                </div>

                <table v-else class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th class="shx-th-80"></th>
                            <th v-for="(col, i) in columns" :key="col" :class="{ 'text-right': i === admissionsIndex }">
                                {{ col }}
                            </th>
                            <th class="text-right shx-th-80">Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, r) in rows" :key="r" :class="rowClass(row)">
                            <td><span class="badge" :class="badgeClass(row)">{{ row.type }}</span></td>
                            <td v-for="(col, i) in columns" :key="col" :class="{ 'text-right': i === admissionsIndex }">
                                <template v-if="i === admissionsIndex">
                                    <span v-if="row.type === 'changed'">
                                        <s class="text-muted">{{ row.before }}</s> → <strong>{{ row.after }}</strong>
                                    </span>
                                    <span v-else>{{ row.fields[i] }}</span>
                                </template>
                                <template v-else>{{ row.fields[i] }}</template>
                            </td>
                            <td class="text-right" :class="deltaClass(row.after - row.before)">
                                {{ formatDelta(row.after - row.before) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p class="text-muted small mt-2 mb-0">
                    Rows are paired on every column except <code>ADMISSIONS</code>: a row that only changed its
                    admissions shows as <em>changed</em> (old → new), otherwise it is <em>added</em> or
                    <em>removed</em>. Rows that merely moved within the file are not shown.
                    <em>(nothing)</em> as From compares against an empty file.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AdmissionsSource',
    props: {
        file: { type: String, required: true },
    },
    data() {
        return {
            loading: true,
            day: null,
            revisions: [],
            from: undefined,
            to: null,
            columns: [],
            rows: [],
            summary: { added: 0, removed: 0, changed: 0, admissions_before: 0, admissions_after: 0 },
        };
    },
    computed: {
        admissionsIndex() {
            const i = this.columns.indexOf('ADMISSIONS');
            return i === -1 ? this.columns.length - 1 : i;
        },
    },
    created() {
        if ('from' in this.$route.query) {
            this.from = this.$route.query.from || null;
        }
        this.to = this.$route.query.to || null;
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            const params = {};
            if (this.from !== undefined) {
                params.from = this.from === null ? '' : this.from;
            }
            if (this.to) {
                params.to = this.to;
            }
            axios
                .get('/admissions/sources/' + this.file, { params })
                .then(response => {
                    const data = response.data.data;
                    this.day = data.day;
                    this.revisions = data.revisions || [];
                    this.from = data.from;
                    this.to = data.to;
                    this.columns = data.diff.header || [];
                    this.rows = data.diff.rows || [];
                    this.summary = data.diff.summary || this.summary;
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        reload() {
            const query = { to: this.to, from: this.from === null ? '' : this.from };
            this.$router.replace({ query }).catch(() => {});
            this.load();
        },
        revisionLabel(rev) {
            const n = this.revisions.length - this.revisions.indexOf(rev);
            return `#${n} · ${rev.date.replace('T', ' ').substring(0, 16)} · ${rev.hash.substring(0, 7)}`;
        },
        rowClass(row) {
            return 'diff-row diff-row-' + row.type;
        },
        badgeClass(row) {
            return { 'badge-success': row.type === 'added', 'badge-danger': row.type === 'removed', 'badge-warning': row.type === 'changed' };
        },
        deltaClass(delta) {
            return delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted';
        },
        formatDelta(delta) {
            return (delta > 0 ? '+' : '') + Math.round(delta).toLocaleString();
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>

<style scoped>
.diff-row-added td {
    background-color: rgba(40, 167, 69, 0.18);
}
.diff-row-removed td {
    background-color: rgba(220, 53, 69, 0.18);
}
.diff-row-changed td {
    background-color: rgba(255, 193, 7, 0.18);
}
.diff-row-added td:first-child {
    box-shadow: inset 3px 0 0 #28a745;
}
.diff-row-removed td:first-child {
    box-shadow: inset 3px 0 0 #dc3545;
}
.diff-row-changed td:first-child {
    box-shadow: inset 3px 0 0 #ffc107;
}
</style>
